import Awesomplete from 'awesomplete';
import throttle from 'lodash/throttle';

/*
 * WARNING: all listeners set-up by this module MUST BE REMOVED by the returned destroy() function.
 */
/**
 * This module handles the autocompletion of the website.
 * In particular, it handles the home autocompletion (with categories) but also the FAQ or Profile
 * autocompletion.
 *
 * Therefore, when updating this file, please check *EVERY* place where autocompletion is used.
 *
 * Some explanations before you wonder why I made these choice :
 * - I use explicit index because Awesomplete does not garantee order preservation under all circumstances.
 * Yes it will keep the order most of the time but trust me, as soon as your order is not lexicographic, it will mess
 * your results
 *
 */
Hop.defineModule(
    'autocomplete',
    (
        root,
        {
            suggestEndpoint,
            selectedCategorySelector,
            companySuggest,
            multiple,
            redirectPath,
            onSelect,
            categoryPattern,
            disablePicto,
            minCharBeforeAutoCompletion = 2,
            maxCharForAutoCompletion = 100,
        },
    ) => {
        let container;
        let familyFilter;
        let categoryFilters;
        let selectedCategory;
        let asInputs;
        let debounceDate;

        const RESULT_CLASS = 'awesomplete-list';
        const throttledUpdate = throttle(updateSuggestion, 150, {leading: true});
        const ff4jSearchAutocompleteV2Enabled = Hop.get('/api/feature-flipping/search-autocomplete-new-api')
            .then((result) => {
                return result;
            })
            .catch(() => false);

        function structuredSuggestion(suggestion) {
            if (typeof suggestion === 'string') {
                return {key: suggestion, text: suggestion};
            }
            return suggestion;
        }

        function getContainer() {
            return container ? container : root.closest('form');
        }

        function getFamilyFilter() {
            return familyFilter ? familyFilter : getContainer().querySelectorAll('input[name=f-fam]');
        }

        function getCategoryFilters() {
            return categoryFilters ? categoryFilters : Hop.document.querySelectorAll('input[name=f-cat]');
        }

        function getSelectedCategory() {
            return selectedCategory ? selectedCategory : Hop.document.querySelector(selectedCategorySelector);
        }

        function getAutoSuggestTrackInput() {
            return asInputs ? asInputs : Hop.document.querySelectorAll('input[name=as]');
        }

        let nextItem = true;
        let awesomplete = null;
        let currentIndex = 0;

        checkConfiguration();
        initializeAwesomplete();
        setupEventListeners();

        function checkConfiguration() {
            if (!suggestEndpoint) {
                throw new Error('No endpoint specified for suggestions');
            }
        }

        function initializeAwesomplete() {
            awesomplete = new Awesomplete(root, {
                autoFirst: false,
                data: transformData,
                filter: () => true,
                item: renderItem,
                sort: (a, b) => a.value.index - b.value.index,
            });

            addClassToResultList();

            if (multiple) {
                awesomplete.replace = function (text) {
                    root.value = root.value.match(/^.+,\s*|/)[0] + text + ', ';
                };
            }
        }

        function addClassToResultList() {
            awesomplete.ul.classList.add(RESULT_CLASS);
        }

        function onClick() {
            resetSelectedCategory();
            awesomplete.open();
        }

        function onInput() {
            let query;
            if (multiple) {
                const match = root.value.match(/^(?:.*?,\s*|\s*)([^,]+)$/);
                query = match ? match[1] : root.value;
            } else {
                query = root.value;
            }

            (!root.value || root.value.trim().length <= 1) && reset();

            // we don't "just" trim the input in all cases, as we need spaces when autocompleting multi-words tags
            if (query.trim().length === 0 || query.length < minCharBeforeAutoCompletion) {
                return;
            }

            const sanitizedQuery = query.slice(0, maxCharForAutoCompletion);

            throttledUpdate(sanitizedQuery);
        }

        function onKeyDown(e) {
            handleTab(e);
            handleKeyboardNavigation(e);
        }

        function onAwesompleteHighlight(e) {
            const {value: selectedItem} = e.text;

            // Unselectable item
            if (!selectedItem || selectedItem.type === 'TITLE') {
                e.preventDefault();

                // prevent infinite recursion
                if (!awesomplete.ul.children.length) {
                    return;
                }

                nextItem ? awesomplete.next() : awesomplete.previous();
            }
        }

        function onAwesompleteSelect(e) {
            const {label, value: selectedItem} = e.text;

            if (!selectedItem) {
                awesomplete.close();
                e.preventDefault();
                return;
            }

            if (selectedItem.type === 'CATEGORY') {
                selectCategory(selectedItem);
            } else if (selectedItem.type === 'COMPANY') {
                root.value = selectedItem.data.text;
            } else if (selectedItem.type === 'JOB') {
                root.value = selectedItem.data.text;
            } else {
                if (multiple) {
                    return; // Default behavior
                }
                root.value = selectedItem.data.text;
            }

            selectAutoSuggestTracking();
            dispatchAutocompleteSelect();
            awesomplete.close();
            e.preventDefault();

            if (redirectPath) {
                window.location.href = redirectPath + encodeURIComponent(selectedItem.data.key);
            }

            Hop.safeGA('send', 'event', 'suggestion', 'validated', label, 1);

            setTimeout(() => {
                if (multiple) {
                    root.focus();
                } else if (onSelect && onSelect.focus) {
                    // Allows to trigger focus on other field
                    document.querySelector(onSelect.focus).focus();
                } else if (onSelect && onSelect.eventTrigger) {
                    Hop.body.dispatchEvent(
                        new CustomEvent(onSelect.eventTrigger.eventType, {
                            detail: selectedItem.data[onSelect.eventTrigger.eventData],
                        }),
                    );
                }
            }, 50);
        }

        function dispatchAutocompleteSelect() {
            root.dispatchEvent(
                new Event('autofill-input', {
                    bubbles: true,
                }),
            );
        }

        function onSearchRefresh() {
            awesomplete.close();
        }

        function onSearchRefreshed() {
            awesomplete.close();
        }

        function setupEventListeners() {
            root.addEventListener('click', onClick);
            root.addEventListener('input', onInput);
            root.addEventListener('keydown', onKeyDown);
            root.addEventListener('awesomplete-highlight', onAwesompleteHighlight);
            root.addEventListener('awesomplete-select', onAwesompleteSelect, false);
            Hop.events.on('search:refresh', onSearchRefresh);
            Hop.events.on('search:refreshed', onSearchRefreshed);
        }

        /**
         * Transform each data item to a {label, value} pattern
         *
         * @param {string} suggestion
         * @return {Object} The suggestion.
         */
        function transformData(suggestion) {
            let label;
            if (suggestion.type === 'CATEGORY') {
                label =
                    getPicto(suggestion.type) +
                    Hop.messageTemplate(categoryPattern, Hop.escapeHtml(suggestion.data.topSuggestion), Hop.escapeHtml(suggestion.data.text));
            } else if (suggestion.type === 'TITLE') {
                label = Hop.escapeHtml(suggestion.data.text);
            } else if (suggestion.type === 'COMPANY') {
                const companyArea =
                    suggestion.data.area !== null ? spanify('autocomplete-item-company-infos', Hop.escapeHtml(suggestion.data.area)) : '';
                const companySize =
                    suggestion.data.area !== null ? spanify('autocomplete-item-company-infos', Hop.escapeHtml(suggestion.data.companySize)) : '';
                const separator = companyArea !== '' && companySize !== '' ? ' - ' : '';
                const companyInfo = companyArea + separator + companySize;
                label = spanify('autocomplete-item-company', Hop.escapeHtml(suggestion.data.text)) + companyInfo;
            } else {
                label =
                    (suggestion.noSuggestionFound ? '' : getPicto(suggestion.type)) +
                    spanify('autocomplete-item-text', Hop.escapeHtml(suggestion.data.text));
            }
            return {
                label,
                value: suggestion,
            };
        }

        /**
         * Renders each suggestion item to a custom list element
         *
         * @param {Object} item The item.
         * @return {Object} The DOM node.
         */
        function renderItem(item) {
            const {value: suggestion, label} = item;
            return Awesomplete.$.create('li', {
                innerHTML: label,
                class: 'awesomplete-list--item autocomplete-' + suggestion.type.toLowerCase(),
            });
        }

        function reset() {
            resetSelectedCategory();
            awesomplete.list = [];
            currentIndex = 0;
            Hop.document.querySelector('.awesomplete > ul').innerHTML = '';
        }

        function resetSelectedCategory() {
            const element = getSelectedCategory();
            if (element) {
                element.textContent = '';
            }
        }

        function selectCategory(selectedItem) {
            resetSelectedCategory();

            root.value = selectedItem.data.term;

            const element = getSelectedCategory();
            if (element) {
                element.textContent = selectedItem.data.text;
            }

            getFamilyFilter().value = selectedItem.data.family;

            const categoriesElements = getCategoryFilters();
            for (let i = 0; i < categoriesElements.length; ++i) {
                categoriesElements[i].value = selectedItem.data.key;
            }
        }

        function fixSuggestionListSize() {
            const autocompleteList = Hop.document.querySelector('.awesomplete > ul');
            const autocompleteListOffsetTop = $(autocompleteList).offset().top;
            const windowHeight = window.innerHeight;
            const bodyScrollTop = document.body.scrollTop;
            const maxHeightAutocompleteList = windowHeight + bodyScrollTop - autocompleteListOffsetTop;

            autocompleteList.style.maxHeight = maxHeightAutocompleteList + 'px';
        }

        function updateSuggestion(terms) {
            resetSelectedCategory();
            resetAutoSuggestTracking();

            const currentDate = new Date();
            debounceDate = currentDate;

            fetch &&
                fetch(suggestEndpoint + encodeURIComponent(terms), {credentials: 'same-origin'})
                    .then((response) => response.json())
                    .then((data) => {
                        if (debounceDate > currentDate) {
                            return;
                        }
                        // dirty reformatting of data
                        if (companySuggest) {
                            data = {
                                suggestions: [],
                                companies: data.map((elt) => ({
                                    text: elt.name,
                                    key: elt.id,
                                    area: elt.area,
                                    companySize: elt.companySize,
                                })),
                            };
                        }

                        const jobs = data.jobs ? data.jobs.map((job) => ({index: ++currentIndex, type: 'JOB', data: job})) : [];

                        /* eslint-disable indent */
                        const companies = data.companies
                            ? data.companies.map((company) => ({
                                  index: ++currentIndex,
                                  type: 'COMPANY',
                                  data: company,
                              }))
                            : [];
                        const categories = data.categories
                            ? data.categories.map((category) => {
                                  category.topSuggestion = category.term;
                                  return {index: ++currentIndex, type: 'CATEGORY', data: category};
                              })
                            : [];
                        const suggestions = data.suggestions
                            ? data.suggestions.map((suggestion) => ({
                                  index: ++currentIndex,
                                  type: 'SUGGESTION',
                                  data: ff4jSearchAutocompleteV2Enabled ? structuredSuggestion(suggestion) : suggestion,
                                  noSuggestionFound: data.noSuggestionFound || false,
                              }))
                            : [];
                        /* eslint-enable indent */

                        const isFocused = document.activeElement === root;
                        const list = data.suggestions ? [...jobs, ...categories, ...companies, ...suggestions] : [];

                        awesomplete.list = list;
                        awesomplete.evaluate();

                        fixSuggestionListSize();

                        // If the element is not focused anymore, do not display suggestion list.
                        if (!isFocused) {
                            awesomplete.close();
                        }
                    })
                    .catch((err) => {
                        // console.log('fetch and update suggestions failed', err);
                        return Promise.reject(err);
                    });
        }

        function handleTab(e) {
            // If tab is pressed, validate the selected value before
            // switching to the next field
            if (e.keyCode === 9) {
                awesomplete.select();
            }
        }

        function handleKeyboardNavigation(e) {
            if (e.keyCode === 40) {
                nextItem = true;
            }
            if (e.keyCode === 38) {
                nextItem = false;
            }
        }

        function spanify(spanClass, content) {
            return `<span class='${spanClass}'>${content}</span>`;
        }

        function selectAutoSuggestTracking() {
            const elements = getAutoSuggestTrackInput();

            for (let i = 0; i < elements.length; i++) {
                elements[i].value = 't';
            }
        }

        function resetAutoSuggestTracking() {
            const elements = getAutoSuggestTrackInput();

            for (let i = 0; i < elements.length; i++) {
                elements[i].value = '';
            }
        }

        function getPicto(type) {
            if (disablePicto) {
                return '';
            }
            const pictoMap = {SUGGESTION: 'skills', CATEGORY: 'category', JOB: 'job'};
            return `<img class='autocomplete-item-picto' src='/img/autocomplete/${pictoMap[type]}.svg'>`;
        }

        return function destroy() {
            root.removeEventListener('click', onClick);
            root.removeEventListener('input', onInput);
            root.removeEventListener('keydown', onKeyDown);
            root.removeEventListener('awesomplete-highlight', onAwesompleteHighlight);
            root.removeEventListener('awesomplete-select', onAwesompleteSelect, false);
            Hop.events.off('search:refresh', onSearchRefresh);
            Hop.events.off('search:refreshed', onSearchRefreshed);
        };
    },
);
